import { supabase, LabNotificationLog } from '../lib/supabase';
import { getTenantId } from '../contexts/TenantContext';

export const labNotificationService = {
  async getAll(): Promise<LabNotificationLog[]> {
    const tenantId = getTenantId();
    const { data, error } = await supabase
      .from('lab_notification_log')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<LabNotificationLog | null> {
    const tenantId = getTenantId();
    const { data, error } = await supabase
      .from('lab_notification_log')
      .select('*')
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .single();

    if (error) throw error;
    return data;
  },

  async create(notification: Omit<LabNotificationLog, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>): Promise<LabNotificationLog> {
    const tenantId = getTenantId();
    const { data, error } = await supabase
      .from('lab_notification_log')
      .insert({ ...notification, tenant_id: tenantId })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, notification: Partial<LabNotificationLog>): Promise<LabNotificationLog> {
    const tenantId = getTenantId();
    const { data, error } = await supabase
      .from('lab_notification_log')
      .update(notification)
      .eq('id', id)
      .eq('tenant_id', tenantId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async markAsSent(id: string): Promise<LabNotificationLog> {
    return this.update(id, {
      status: 'sent',
      sent_at: new Date().toISOString()
    });
  },

  async markAsDelivered(id: string): Promise<LabNotificationLog> {
    return this.update(id, {
      status: 'delivered',
      delivered_at: new Date().toISOString()
    });
  },

  async markAsRead(id: string): Promise<LabNotificationLog> {
    return this.update(id, {
      status: 'read',
      read_at: new Date().toISOString()
    });
  },

  async markAsFailed(id: string, errorMessage: string): Promise<LabNotificationLog> {
    return this.update(id, {
      status: 'failed',
      error_message: errorMessage
    });
  },

  async getByOrderId(orderId: string): Promise<LabNotificationLog[]> {
    const tenantId = getTenantId();
    const { data, error } = await supabase
      .from('lab_notification_log')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('order_id', orderId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getByPatientId(patientId: string): Promise<LabNotificationLog[]> {
    const tenantId = getTenantId();
    const { data, error } = await supabase
      .from('lab_notification_log')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('patient_id', patientId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getPendingNotifications(): Promise<LabNotificationLog[]> {
    const tenantId = getTenantId();
    const { data, error } = await supabase
      .from('lab_notification_log')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('status', 'pending')
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async getFailedNotifications(): Promise<LabNotificationLog[]> {
    const tenantId = getTenantId();
    const { data, error } = await supabase
      .from('lab_notification_log')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('status', 'failed')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getNotificationStats(dateRange?: { start: string; end: string }) {
    const tenantId = getTenantId();
    let query = supabase
      .from('lab_notification_log')
      .select('*')
      .eq('tenant_id', tenantId);

    if (dateRange) {
      query = query
        .gte('created_at', dateRange.start)
        .lte('created_at', dateRange.end);
    }

    const { data, error } = await query;
    if (error) throw error;

    const notifications = data || [];
    
    return {
      total: notifications.length,
      sent: notifications.filter(n => n.status === 'sent').length,
      delivered: notifications.filter(n => n.status === 'delivered').length,
      failed: notifications.filter(n => n.status === 'failed').length,
      pending: notifications.filter(n => n.status === 'pending').length,
      read: notifications.filter(n => n.status === 'read').length,
      byType: this.groupByType(notifications),
      byMethod: this.groupByMethod(notifications),
      deliveryRate: this.calculateDeliveryRate(notifications)
    };
  },

  async sendCriticalValueAlert(orderId: string, patientId: string, doctorId: string, testName: string, criticalValue: string): Promise<LabNotificationLog[]> {
    const notifications = [];
    
    const doctorNotification = await this.create({
      order_id: orderId,
      patient_id: patientId,
      doctor_id: doctorId,
      notification_type: 'critical_value',
      notification_method: 'sms',
      recipient_type: 'doctor',
      recipient_id: doctorId,
      subject: 'Critical Lab Value Alert',
      message: `CRITICAL: ${testName} result is ${criticalValue}. Please review immediately.`,
      status: 'pending'
    });
    notifications.push(doctorNotification);

    const adminNotification = await this.create({
      order_id: orderId,
      patient_id: patientId,
      notification_type: 'critical_value',
      notification_method: 'in_app',
      recipient_type: 'admin',
      subject: 'Critical Lab Value Reported',
      message: `Critical value reported for ${testName}: ${criticalValue}`,
      status: 'pending'
    });
    notifications.push(adminNotification);

    return notifications;
  },

  groupByType(notifications: LabNotificationLog[]) {
    const grouped = notifications.reduce((acc, notification) => {
      const type = notification.notification_type;
      if (!acc[type]) {
        acc[type] = { total: 0, sent: 0, failed: 0 };
      }
      acc[type].total++;
      if (notification.status === 'sent' || notification.status === 'delivered') acc[type].sent++;
      if (notification.status === 'failed') acc[type].failed++;
      return acc;
    }, {} as Record<string, any>);

    return Object.entries(grouped).map(([type, stats]) => ({
      type,
      ...stats
    }));
  },

  groupByMethod(notifications: LabNotificationLog[]) {
    const grouped = notifications.reduce((acc, notification) => {
      const method = notification.notification_method;
      if (!acc[method]) {
        acc[method] = { total: 0, sent: 0, failed: 0 };
      }
      acc[method].total++;
      if (notification.status === 'sent' || notification.status === 'delivered') acc[method].sent++;
      if (notification.status === 'failed') acc[method].failed++;
      return acc;
    }, {} as Record<string, any>);

    return Object.entries(grouped).map(([method, stats]) => ({
      method,
      ...stats
    }));
  },

  calculateDeliveryRate(notifications: LabNotificationLog[]): number {
    const delivered = notifications.filter(n => n.status === 'delivered' || n.status === 'read').length;
    const total = notifications.length;
    return total > 0 ? (delivered / total * 100) : 0;
  }
};
